import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Link2, Loader2, Search, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { creditSense } from "@/lib/api";
import { StatusPill, fmtTs } from "./creditSenseShared";

// "Attach to application" — same wizard shape as the Sumsub hub: matches pre-selected, SFA search, what to post/upload, note.
export function CreditSenseAttachDialog({ appId, onClose, onDone }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [picked, setPicked] = useState(null);
  const [q, setQ] = useState("");
  const [searching, setSearching] = useState(false);
  const [note, setNote] = useState("");
  const [opts, setOpts] = useState({ post_update: true, attach_report: true, attach_statements: true, attach_json: false, set_ob: true });
  const [busy, setBusy] = useState(false);

  const load = async (query = "") => {
    try {
      query ? setSearching(true) : setData(null);
      const r = await creditSense.matches(appId, query);
      setData((prev) => (query && prev ? { ...prev, items: r.items } : r));
      if (!query) {
        const conf = (r.items || []).filter((m) => m.confident);
        setPicked(conf.length === 1 ? conf[0] : r.items?.length === 1 ? r.items[0] : null);
      } else if (r.items?.length === 1) setPicked(r.items[0]);
      setErr(null);
    } catch (e) { setErr(e.response?.data?.detail || e.message); }
    finally { setSearching(false); }
  };
  useEffect(() => { load(); }, [appId]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async () => {
    if (!picked) { toast.error("Pick the application first"); return; }
    setBusy(true);
    try {
      const r = await creditSense.attach(appId, { item_id: picked.item_id, sf: picked.sf, name: picked.name, note, ...opts });
      (r.warnings || []).forEach((w) => toast.warning(w, { duration: 9000 }));
      toast.success(`Attached to ${r.sf}${r.files?.length ? ` · ${r.files.length} file${r.files.length === 1 ? "" : "s"} uploaded` : ""}${r.skipped?.length ? ` · ${r.skipped.length} already there` : ""}${r.ob_set ? " · OB → Connected" : ""}`);
      onDone(r);
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || e.message, { duration: 9000 }); }
    finally { setBusy(false); }
  };

  const ap = data?.app;
  const items = data?.items || [];
  const pdfs = (ap?.files || []).filter((f) => f.content_type === "pdf");
  const nReport = pdfs.filter((f) => f.kind === "report").length, nStmts = pdfs.filter((f) => f.kind !== "report").length;
  const opt = (k, label, testId) => <label className="flex items-center gap-1.5"><Checkbox checked={opts[k]} onCheckedChange={(v) => setOpts((o) => ({ ...o, [k]: !!v }))} data-testid={testId} />{label}</label>;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" aria-describedby={undefined} data-testid="creditsense-attach-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base"><Link2 className="w-4 h-4 text-blue-600" />Attach bank statements to an application</DialogTitle>
        </DialogHeader>
        {!data && !err ? (
          <div className="p-8 text-center text-sm text-muted-foreground"><Loader2 className="mx-auto mb-2 w-5 h-5 animate-spin" />Matching the applicant to Monday…</div>
        ) : err ? (
          <div className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700" data-testid="creditsense-attach-error">{err}</div>
        ) : (
          <div className="space-y-3">
            <div className="flex flex-wrap items-center gap-2 rounded border border-slate-200 bg-slate-50 px-2.5 py-2 text-xs" data-testid="creditsense-attach-applicant">
              <span className="font-medium text-slate-900">{ap?.applicant || "Unnamed applicant"}</span>
              <StatusPill status={ap?.status} />
              {ap?.business_name && <span className="text-slate-600">· {ap.business_name}</span>}
              {ap?.banks?.length > 0 && <span className="text-slate-500">· {ap.banks.join(", ")}</span>}
              {ap?.app_reference && <span className="font-mono text-[11px] text-slate-500">· ref {ap.app_reference}</span>}
              {data?.linked?.sf && <Badge variant="outline" className="ml-auto border-emerald-300 bg-emerald-50 text-[10px] text-emerald-800" data-testid="creditsense-attach-already">already attached to {data.linked.sf}</Badge>}
            </div>
            <div>
              <div className="mb-1 flex items-center gap-1.5 text-[11px] font-medium text-slate-600"><Sparkles className="w-3 h-3 text-violet-600" />Application ({items.length} match{items.length === 1 ? "" : "es"} — SFA in the reference · applicant name · business name against Monday)</div>
              {items.length === 0 && <div className="mb-1 rounded border border-amber-300 bg-amber-50 p-2 text-[11px] text-amber-800" data-testid="creditsense-attach-nomatch">No confident match — search by SFA number or company below.</div>}
              <div className="max-h-44 divide-y overflow-y-auto rounded border" data-testid="creditsense-attach-matches">
                {items.map((m) => (
                  <label key={m.item_id} className="flex cursor-pointer items-center gap-2 px-2 py-1.5 text-xs hover:bg-slate-50">
                    <input type="radio" name="creditsense-attach-target" checked={picked?.item_id === m.item_id} onChange={() => setPicked(m)} data-testid={`creditsense-attach-target-${m.sf}`} />
                    <span className="font-mono font-semibold">{m.sf}</span>
                    <span className="flex-1 truncate">{m.name}{m.contact ? <span className="text-slate-400"> · {m.contact}</span> : null}</span>
                    {m.score > 0 && <Badge variant="outline" className={`text-[9px] ${m.confident ? "border-emerald-300 bg-emerald-50 text-emerald-800" : "text-slate-500"}`} title={(m.reasons || []).join(", ")}>{m.score}%</Badge>}
                    <Badge variant="outline" className="text-[9px]">{m.status || "—"}</Badge>
                    <span className="shrink-0 text-[10px] text-muted-foreground">{m.date ? fmtTs(m.date).slice(0, 11) : ""}</span>
                  </label>
                ))}
              </div>
              <div className="mt-1.5 flex gap-1">
                <Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && q.trim() && load(q.trim())} placeholder="Search by SFA number or company, e.g. SFA-3451" className="h-7 text-xs" data-testid="creditsense-attach-search" />
                <Button size="sm" variant="outline" className="h-7 px-2" disabled={searching || !q.trim()} onClick={() => load(q.trim())} data-testid="creditsense-attach-search-btn">
                  {searching ? <Loader2 className="w-3 h-3 animate-spin" /> : <Search className="w-3 h-3" />}
                </Button>
              </div>
            </div>
            <div className="grid gap-1.5 text-[11px] sm:grid-cols-2" data-testid="creditsense-attach-options">
              {opt("post_update", "Post the bank-statement summary as a Monday update", "creditsense-attach-post")}
              {opt("attach_report", `Upload the Credit Sense report PDF${nReport ? ` (${nReport})` : ""}`, "creditsense-attach-report")}
              {opt("attach_statements", `Upload the bank-statement PDFs${nStmts ? ` (${nStmts})` : ""}`, "creditsense-attach-statements")}
              {opt("set_ob", "Set OB → Connected on the item", "creditsense-attach-ob")}
              {opt("attach_json", "Also upload the raw JSON data file", "creditsense-attach-json")}
            </div>
            <div>
              <div className="mb-1 text-[11px] font-medium text-slate-600">Note added under the summary (optional)</div>
              <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} className="text-xs" placeholder="e.g. 90 days of trading statements — ready for UW" data-testid="creditsense-attach-note" />
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={busy} data-testid="creditsense-attach-cancel">Cancel</Button>
          <Button onClick={submit} disabled={busy || !data || !picked} data-testid="creditsense-attach-submit">
            {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Link2 className="w-4 h-4 mr-1" />}
            {busy ? "Attaching…" : `Attach${picked ? ` to ${picked.sf}` : ""}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
